import {Component, EventEmitter, Input, Output, ChangeDetectionStrategy} from '@angular/core';
import {BerthVisit, LocalMovement, OrderStatus, Visit} from '@portbase/bezoekschip-service-typescriptmodels';
import moment from 'moment';
import {AppContext} from 'src/app/app-context';
import {VisitContext} from '../visit-context';
import {ShipMovementService} from '../visit/ship-movement.service';
import {OrderMovementService} from '../visit/nautical-services/order-movement.service';
import {cloneObject} from "../../common/utils";
import {CdkDrag, CdkDragDrop, CdkDragStart, CdkDropList, moveItemInArray} from "@angular/cdk/drag-drop";

@Component({
    selector: 'app-timeline',
    templateUrl: './timeline.component.html',
    styleUrls: ['./timeline.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TimelineComponent {
  context = VisitContext;
  appContext = AppContext;

  @Input() berthVisits: BerthVisit[];
  @Input() readonly: boolean;
  @Output() berthVisitsChange = new EventEmitter<BerthVisit[]>();

  constructor(private shipMovementService: ShipMovementService, private orderMovementService: OrderMovementService) {
  }

  onBerthVisitDrop(event: CdkDragDrop<BerthVisit[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(this.berthVisits, event.previousIndex, event.currentIndex);
    const berthVisit: BerthVisit = event.item.data

    if (!VisitContext.visit.terminalPlanningEnabled) {
      berthVisit.eta = null;
      berthVisit.etd = null;
    }

    if (this.context.visit.orderIncomingMovement && event.currentIndex == 0) {
      this.context.updateCalculatedEtas();
      this.context.clearDependency();
    }

    this.berthVisitsChange.emit(this.berthVisits);
  }

  isDragDisabled(berthVisit: BerthVisit): boolean {
    return !!berthVisit.ata || !! berthVisit.atd || this.context.isVisitReadonly();
  }
  
  canSortPredicate = (index: number, drag: CdkDrag, dropList: CdkDropList<BerthVisit[]>): boolean => {
    const siblingIndex = index + 1;
    const sortPreview = [...this.berthVisits];
    moveItemInArray(sortPreview, dropList.getSortedItems().indexOf(drag), index);
    return !sortPreview
      .slice(siblingIndex)
      .some(berthVisit => !!berthVisit.ata || !!berthVisit.atd);
  }

  onDragStarted(event: CdkDragStart) {
    const activeElement = document.activeElement as HTMLElement;

    if (
      activeElement &&
      (activeElement.closest('.prevent-drag-move'))
    ) {
      event.source.reset();  // cancel the drag
    }
  }

  getShipStatus(berthVisit: BerthVisit): string {
    return this.shipMovementService.shipStatus(berthVisit);
  }

  arrivalTime(berthVisit: BerthVisit) {
    return berthVisit.ata ? berthVisit.ata : berthVisit.eta || berthVisit.requestedEta;
  }

  departureTime(berthVisit: BerthVisit) {
    return berthVisit.atd ? berthVisit.atd : berthVisit.etd || berthVisit.requestedEtd;
  }

  userIsNextOwnerOrDeclarant() {
    return VisitContext.isOrganisationNextDeclarant();
  }

  getStatusOfIncomingOrder(visit: Visit): OrderStatus {
    return visit.incomingMovementHarbourMasterInfo.orderStatus;
  }

  orderMovement(berthVisit: BerthVisit) {
    this.orderMovementService.orderMovement(berthVisit);
    berthVisit.nextMovement.orderCancellationReason = null;
  }

  orderIncomingMovement() {
    this.context.visit.visitDeclaration.portVisit.firstMovement.order = true;
    this.context.visit.visitDeclaration.portVisit.firstMovement.orderCancellationReason = null;
  }

  cancelMovement(berthVisit: BerthVisit) {
    this.orderMovementService.cancelMovement(berthVisit);
  }

  get firstBerthVisitId(): string {
    return this.berthVisits[0]?.id;
  }

  afterEtaPort() {
    let etaPort = this.context.visit.visitDeclaration.portVisit.etaPort;
    let now = moment();
    if (this.context.visit.orderIncomingMovement) {
      return !!etaPort && moment(now).isAfter(etaPort) && this.context.visit.incomingMovementHarbourMasterInfo && !!this.context.visit.visitDeclaration.portVisit.firstMovement.order
        && (this.context.visit.incomingMovementHarbourMasterInfo.orderStatus === 'CONFIRMED' || this.context.visit.incomingMovementHarbourMasterInfo.orderStatus === 'CONFIRMED_NEW_PROPOSAL');
    } else {
      return !!etaPort && moment(now).isAfter(etaPort);
    }
  }

  isBetweenBerthsOrDeparting(idx: number) {
    let berthVisits = this.berthVisits;
    if (berthVisits.length > idx + 1) {
      return !!berthVisits[idx + 1].ata || !!berthVisits[idx + 1].atd;
    } else if (berthVisits.length === idx + 1) {
      return !!berthVisits[idx].atd && !!this.context.visit.visitDeclaration.portVisit.atdPort;
    }
    return false;
  }

  copyMovement() {
    this.berthVisits[0].nextMovement ||= <LocalMovement>{};
    let target = this.berthVisits[0].nextMovement;
    let source = cloneObject(this.context.visit.visitDeclaration.portVisit.firstMovement);
    ["vesselDraft", "vesselMasterName", "numberOfCrew", "numberOfPassengers", "cargo", "pilotExemption", "offStandardBeam"].forEach(property => {
      target[property] = source[property];
    });
  }

  copyNauticalServices() {
    let berthVisits = this.berthVisits;
    berthVisits[0].tugboatAtDeparture = cloneObject(berthVisits[0].tugboatAtArrival);
    berthVisits[0].boatmenAtDeparture = cloneObject(berthVisits[0].boatmenAtArrival);
    berthVisits[0].helmsmenAtDeparture = cloneObject(berthVisits[0].helmsmenAtArrival);
    berthVisits[0].nextMovement.pilotService = cloneObject(this.context.visit.visitDeclaration.portVisit.firstMovement.pilotService);
    if (berthVisits.length > 1) {
      berthVisits[1].tugboatAtArrival = cloneObject(berthVisits[0].tugboatAtArrival);
      berthVisits[1].boatmenAtArrival = cloneObject(berthVisits[0].boatmenAtArrival);
      berthVisits[1].helmsmenAtArrival = cloneObject(berthVisits[0].helmsmenAtArrival);
    }
  }

  anyNextBerthVisitHasAta(idx: number) {
    let berthVisits = this.berthVisits;
    if (idx === berthVisits.length) {
      return false;
    }
    for (let i = idx; i < berthVisits.length; i++) {
      if (berthVisits[i].ata) {
        return true;
      }
    }
    return false;
  }
}
